import React, { useState } from 'react';
import { Send, CheckCircle, MessageSquare, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;
const SUBJECTS = ["Support technique", "Question sur mon abonnement", "Signalement de contenu", "Partenariat", "Presse", "Autre"];

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: 'Support technique', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 50) { toast.error('Le message doit contenir au moins 50 caractères'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Contact — Kurso</title>
        <meta name="description" content="Contactez l'équipe Kurso. Support technique, partenariats, questions sur votre abonnement." />
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold text-white mb-2">Contactez-nous</h1>
          <p className="text-[#a0a0ab]">Nous répondons sous <strong className="text-white">48h ouvrées</strong>.</p>
        </div>

        {submitted ? (
          <div className="kurso-card p-8 text-center" data-testid="contact-success">
            <CheckCircle size={40} className="text-[#06d6a0] mx-auto mb-4" />
            <h2 className="text-white font-bold text-xl mb-2">Message envoyé !</h2>
            <p className="text-[#a0a0ab]">Nous avons bien reçu votre message. Notre équipe vous répondra sous 48h ouvrées.</p>
            <a href="/faq" className="inline-block mt-6 text-[#ff6b6b] hover:underline text-sm">Consulter le Centre d'aide →</a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="kurso-card p-6 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#a0a0ab] mb-1.5">Nom complet</label>
                <input
                  type="text" required value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  data-testid="contact-name-input"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                />
              </div>
              <div>
                <label className="block text-sm text-[#a0a0ab] mb-1.5">Email</label>
                <input
                  type="email" required value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  data-testid="contact-email-input"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#a0a0ab] mb-1.5">Sujet</label>
              <style>{`
                #contact-subject-select option {
                  background: #1a1828;
                  color: #fffffe;
                }
                #contact-subject-select option:hover,
                #contact-subject-select option:checked {
                  background: #ff6b6b;
                  color: #ffffff;
                }
              `}</style>
              <select
                id="contact-subject-select"
                value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                data-testid="contact-subject-select"
                style={{
                  background: '#1a1828',
                  color: '#fffffe',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '10px 16px',
                  width: '100%',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                {SUBJECTS.map(s => (
                  <option key={s} value={s} style={{ background: '#1a1828', color: '#fffffe' }}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-[#a0a0ab] mb-1.5">Message <span className="text-xs">(min 50 caractères)</span></label>
              <textarea
                required value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                rows={6} data-testid="contact-message-input"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#ff6b6b]/50 resize-none"
              />
              <p className="text-xs text-[#a0a0ab] mt-1">{form.message.length} / 50 min</p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                className="flex items-center gap-2 bg-[#ff6b6b] text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-[#ff5555] transition-colors disabled:opacity-50">
                <Send size={14} /> {loading ? 'Envoi...' : 'Envoyer'}
              </button>
              <div className="flex items-center gap-1.5 text-xs text-[#a0a0ab]">
                <Clock size={12} /> Réponse sous 48h ouvrées
              </div>
            </div>
          </form>
        )}

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a href="/faq" className="kurso-card p-4 hover:border-[#ff6b6b]/30 transition-colors group">
            <MessageSquare size={18} className="text-[#ff6b6b] mb-2" />
            <p className="text-white text-sm font-medium group-hover:text-[#ff6b6b] transition-colors">Centre d'aide</p>
            <p className="text-[#a0a0ab] text-xs">Trouvez des réponses aux questions fréquentes</p>
          </a>
          <div className="kurso-card p-4">
            <Send size={18} className="text-[#06d6a0] mb-2" />
            <p className="text-white text-sm font-medium">Email direct</p>
            <a href="mailto:support@mykurso.fr" className="text-[#a0a0ab] text-xs hover:text-[#ff6b6b]">support@mykurso.fr</a>
          </div>
        </div>
      </div>
    </Layout>
  );
}
